import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import crmService from '../../../services/crmService';

const AssignLeadModal = ({ leadIds = [], telecallers = [], onClose, onLeadsAssigned }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTelecaller, setSelectedTelecaller] = useState('');

  const handleAssign = async () => {
    if (!selectedTelecaller) {
      setError('Please select a telecaller');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Assign leads one by one
      const assignedLeads = [];
      for (const leadId of leadIds) {
        const result = await crmService.assignLeadToTelecaller(leadId, selectedTelecaller);
        if (result?.success) {
          // Get updated lead data
          const leadsResult = await crmService.getLeads({ telecaller_id: selectedTelecaller });
          if (leadsResult?.success) {
            const assignedLead = leadsResult.data.find(lead => lead.id === leadId);
            if (assignedLead) {
              assignedLeads.push(assignedLead);
            }
          }
        }
      }

      if (assignedLeads.length > 0) {
        onLeadsAssigned?.(assignedLeads);
      } else {
        setError('Failed to assign leads');
      }

    } catch (error) {
      setError('An unexpected error occurred while assigning leads');
      console.log('Lead assignment error:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedTelecallerName = telecallers?.find(t => t.id === selectedTelecaller)?.full_name || '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg shadow-lg w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Assign Leads</h2>
              <p className="text-sm text-muted-foreground">
                Assign {leadIds.length} lead{leadIds.length !== 1 ? 's' : ''} to a telecaller
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2 text-destructive">
                  <Icon name="AlertCircle" size={16} />
                  <p className="font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Lead Count Info */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                <span className="font-medium text-foreground">
                  {leadIds.length} lead{leadIds.length !== 1 ? 's' : ''} selected for assignment
                </span>
              </div>
            </div>

            {/* Telecaller Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Select Telecaller *
              </label>
              
              {telecallers?.length > 0 ? (
                <Select
                  value={selectedTelecaller}
                  onValueChange={setSelectedTelecaller}
                  placeholder="Choose a telecaller..."
                  options={[
                    { value: '', label: 'Choose a telecaller...' },
                    ...telecallers.map(telecaller => ({
                      value: telecaller.id,
                      label: `${telecaller.full_name} (${telecaller.email})`
                    }))
                  ]}
                />
              ) : (
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="text-center mb-3">
                    <Icon name="Users" size={24} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground mb-1">
                      No telecallers available
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                      You need to create telecaller users first
                    </p>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                    <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                      How to add telecallers:
                    </p>
                    <ol className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1 list-decimal list-inside">
                      <li>Open phpMyAdmin or MySQL client</li>
                      <li>Go to <code className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">laborpro_erp</code> database</li>
                      <li>Run SQL: <code className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">INSERT INTO user_profiles (id, email, full_name, role, phone, department, password_hash, created_at, updated_at) VALUES (UUID(), 'telecaller@example.com', 'Telecaller Name', 'telecaller', '+91-9876543210', 'Sales', '$2y$10$N9qo8uLOickgx2ZMRZoMyeIjZAgcfl7p92ldGxad68LJZdL17lhWy', NOW(), NOW());</code></li>
                      <li>Refresh this page</li>
                    </ol>
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                      See <code className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">HOW_TO_ADD_TELECALLERS.md</code> for detailed instructions
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Assignment Preview */}
            {selectedTelecaller && selectedTelecallerName && (
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="ArrowRight" size={16} className="text-primary" />
                  <span className="text-sm text-foreground">
                    Assigning to: <strong>{selectedTelecallerName}</strong>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={loading || !selectedTelecaller || telecallers?.length === 0}
              className="flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              Assign Leads
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignLeadModal;